import { memo } from 'react';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import { DarePlatform } from 'root/src/shared/@types/platforms';
import { calculateMaxDaresByPlatform } from 'root/src/shared/util/calculateMaxDares';
import { formatNumberWithCommas } from 'root/src/shared/util/formatNumberWithCommas';
import { getReadableViewershipPlatform } from 'root/src/shared/platforms/helpers/getReadablePlatform';
import { useUserCreatorData } from 'root/src/client/logic/auth/hooks/useUser';
import { useIsAuthenticated } from 'root/src/client/v2/auth/hooks/useIsAuthenticated';
import { DarePlatformIcon, PlatformIconColor } from 'root/src/client/web/base/CustomButton/PlatformIcon';
import { buildRequirementTiers, getMinimumViewership, getRequirementPlatforms, VIEWERSHIP_METRIC, VIEWERSHIP_UNIT, } from 'root/src/client/v2/_pages/drop/redesign/dropRequirements';
import { cn } from 'root/src/client/v2/common/helpers/cn';
const formatDares = (maxDares) => {
    if (!Number.isFinite(maxDares)) {
        return 'All dares';
    }
    return `${maxDares} ${maxDares === 1 ? 'dare' : 'dares'}`;
};
const formatRange = (from, to) => {
    if (to === undefined) {
        return `${formatNumberWithCommas(from)}+`;
    }
    return `${formatNumberWithCommas(from)} to ${formatNumberWithCommas(to)}`;
};
const PlatformCard = memo(({ platform, drop, viewership, isConnected }) => {
    var _a, _b;
    const breakpoints = (_a = drop.viewershipBreakpoints) === null || _a === void 0 ? void 0 : _a[platform];
    const tiers = buildRequirementTiers(breakpoints).filter(tier => tier.maxDares > 0);
    const minimum = getMinimumViewership(breakpoints);
    const unit = VIEWERSHIP_UNIT[platform];
    const platformLabel = (_b = getReadableViewershipPlatform(platform)) !== null && _b !== void 0 ? _b : platform;
    // Only meaningful once we know the creator's own numbers, so it stays out of the signed-out view.
    const maxDares = isConnected
        ? calculateMaxDaresByPlatform({
            campaign: drop,
            platform,
            averageCcv: platform === DarePlatform.Twitch ? viewership !== null && viewership !== void 0 ? viewership : 0 : 0,
            averageYoutubeViews: platform === DarePlatform.YouTube ? viewership !== null && viewership !== void 0 ? viewership : 0 : 0,
            averageYoutubeShortsViews: platform === DarePlatform.YouTubeShorts ? viewership !== null && viewership !== void 0 ? viewership : 0 : 0,
            averageTikTokViews: platform === DarePlatform.TikTok ? viewership !== null && viewership !== void 0 ? viewership : 0 : 0,
        })
        : undefined;
    const qualifies = maxDares !== undefined && maxDares > 0;
    const missing = minimum !== null && viewership !== undefined ? minimum - viewership : undefined;
    return (<div className="flex flex-col rounded-2xl border border-solid border-white/[0.06] bg-card p-5">
			<div className="flex items-center gap-2.5">
				<span className="flex h-7 w-7 items-center justify-center rounded-full bg-white">
					<DarePlatformIcon size="16" platform={platform} color={PlatformIconColor.Dark}/>
				</span>
				<h3 className="font-display text-sm font-bold text-foreground">{platformLabel}</h3>
			</div>

			<p className="mt-4 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Minimum to accept a dare</p>
			<p className="mt-0.5 text-2xl font-bold text-foreground">
				{minimum === null
            ? 'None'
            : <>{formatNumberWithCommas(minimum)} <span className="text-sm font-medium text-muted-foreground">{unit}</span></>}
			</p>
			{minimum === null && <p className="mt-0.5 text-xs text-muted-foreground">Every {platformLabel} creator can take a dare here.</p>}

			{tiers.length > 0 && (<ul className="mt-4 space-y-1.5 border-t border-solid border-white/[0.06] pt-4">
					{tiers.map(tier => (<li key={tier.from} className="flex items-baseline justify-between gap-3 text-xs">
							<span className="text-muted-foreground">{formatRange(tier.from, tier.to)} {unit}</span>
							<span className="shrink-0 font-semibold text-foreground">{formatDares(tier.maxDares)}</span>
						</li>))}
				</ul>)}

			{isConnected && viewership !== undefined && (<div className={cn('mt-4 flex items-start gap-2 rounded-xl border border-solid p-3 text-xs', qualifies ? 'border-neon-green/30 bg-neon-green/10 text-neon-green' : 'border-white/[0.08] bg-white/[0.03] text-muted-foreground')}>
					{qualifies
                ? <CheckCircleIcon sx={{ fontSize: 16 }} className="mt-px shrink-0"/>
                : <TrendingUpIcon sx={{ fontSize: 16 }} className="mt-px shrink-0"/>}
					<span className="leading-relaxed">
						You average <span className="font-bold">{formatNumberWithCommas(viewership)} {VIEWERSHIP_METRIC[platform]}</span> on {platformLabel}
						{qualifies
                ? <>, so you can accept {formatDares(maxDares).toLowerCase()} here.</>
                : missing !== undefined && missing > 0
                    ? <>, {formatNumberWithCommas(missing)} short of the minimum.</>
                    : <>, which is below the minimum for this drop.</>}
					</span>
				</div>)}
		</div>);
});
PlatformCard.displayName = 'PlatformCard';
export const DropRequirementsSection = memo(({ drop }) => {
    const isAuthenticated = useIsAuthenticated();
    const { viewershipPlatforms, averageCcv, averageYoutubeViews, averageYoutubeShortsViews, averageTikTokViews, } = useUserCreatorData();
    const platforms = getRequirementPlatforms(drop.viewershipBreakpoints);
    const hasConnectedPlatform = platforms.some(platform => viewershipPlatforms.includes(platform));
    const viewershipByPlatform = {
        [DarePlatform.Twitch]: averageCcv,
        [DarePlatform.YouTube]: averageYoutubeViews,
        [DarePlatform.YouTubeShorts]: averageYoutubeShortsViews,
        [DarePlatform.TikTok]: averageTikTokViews,
    };
    return (<section id="requirements" className="scroll-mt-40 px-4 py-12 sm:px-6 lg:px-8">
			<div className="mx-auto max-w-7xl">
				<h2 className="font-display text-2xl font-bold text-foreground sm:text-3xl">Participation Requirements</h2>
				<p className="mt-2 max-w-2xl text-sm text-muted-foreground">
					{platforms.length > 0
            ? 'The publisher sets a viewership bar per platform. It decides whether you can take a dare here, and how many.'
            : 'This drop sets no viewership requirement. Connect a channel and you can take a dare.'}
				</p>

				{/* New creators keep asking whether the allowance refills once they deliver. It does not,
        so the section says it before they hit the limit and wonder why. */}
				{platforms.length > 0 && (<p className="mt-2 max-w-2xl text-sm text-muted-foreground">
						How many dares you can accept is your total per drop. The amount does not reset after dares have been submitted for that drop.
					</p>)}

				{platforms.length > 0 && (<div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
						{platforms.map(platform => (<PlatformCard key={platform} platform={platform} drop={drop} viewership={viewershipByPlatform[platform]} isConnected={viewershipPlatforms.includes(platform)}/>))}
					</div>)}

				{/* Without a connected channel there is no viewership to compare against, so the cards show
        the bar alone. Say why, instead of leaving the "where do I stand" question unanswered. */}
				{platforms.length > 0 && !hasConnectedPlatform && (<p className="mt-4 text-xs text-muted-foreground">
						{isAuthenticated
                ? 'Connect a channel to see where you stand.'
                : 'Sign in and connect your channel to see where you stand.'}
					</p>)}
			</div>
		</section>);
});
DropRequirementsSection.displayName = 'DropRequirementsSection';
